import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import type React from 'react';
import { useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import type { DiscoverySeedFormValues } from './DiscoverySeed';
import { validateCIDRMask } from './isValid';
import type { DiscoverySeedModel } from './model/DiscoverySeedModel';
import { setDiscoverySeed } from './model/command/setDiscoverySeed';

interface DiscoverySeedFormProps {
  model: DiscoverySeedModel;
  control: Control<DiscoverySeedFormValues>;
  setValue: UseFormSetValue<DiscoverySeedFormValues>;
}

export const DiscoverySeedForm: React.FC<DiscoverySeedFormProps> = ({
  model,
  control,
  setValue,
}) => {
  const ip = useWatch({ control, name: 'ip' });
  useEffect(() => {
    if (!isEqual(model?.seedList, ip)) {
      model?.dispatch(setDiscoverySeed, {
        data: {
          seedList: ip,
        },
      });
    }
  }, [ip, model?.dispatch, model?.seedList]);

  useEffect(() => {
    setValue('ip', model?.initialSeedList ?? { ips: [] });
  }, [model?.initialSeedList, setValue]);

  return (
    <div className='pb-sm'>
      A discovery seed is a device from which IP Fabric begins the
      auto-discovery process. These IPs can be device management IP addresses or
      networks. Discovery seed settings do not include or exclude any networks
      from discovery.
      <FormIPSubnetField
        control={control}
        name='ip'
        label='Starting points for network discovery (optional)'
        description='If no seed information is specified, the discovery will begin from the IP Fabric appliance default gateway.'
        placeholder='Insert IP or network'
        customValidation={validateCIDRMasks}
        className='py'
      />
    </div>
  );
};

const validateCIDRMasks = (ipList: string[]) => {
  const validAddresses = ipList.filter((item) => !validateCIDRMask(item));
  const invalidAddresses = ipList.filter((item) => validateCIDRMask(item));
  // reusing undefined returned from find() on purpose as "no error"
  const message = ipList
    .map((item) => validateCIDRMask(item))
    .find((error) => error !== '');

  return {
    // show the first error message until the error is fixed
    message,
    validAddresses,
    invalidAddresses,
  };
};
